/**
 * Utility functions to handle service worker cleanup
 * Particularly useful after payment returns to prevent endless loading
 */

/**
 * Unregisters all service workers and clears caches
 * This is useful when returning from payment to ensure a clean state
 * @returns {Promise<boolean>} True if successful
 */
export async function unregisterServiceWorkers() {
  try {
    // Check if service workers are supported
    if ('serviceWorker' in navigator) {
      // Get all service worker registrations
      const registrations = await navigator.serviceWorker.getRegistrations();

      // Unregister each service worker
      for (const registration of registrations) {
        await registration.unregister();
      }

      // Clear all caches
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(
          cacheNames.map(cacheName => caches.delete(cacheName))
        );
      }

      return true;
    }

    return false;
  } catch (error) {
    return false;
  }
}

/**
 * Reloads the page bypassing the cache
 * This ensures a fresh load after payment
 */
export function reloadPageFresh() {
  // Force reload the page bypassing the cache
  window.location.reload(true);
}

/**
 * Complete cleanup and reload
 * Unregisters service workers, clears caches, and reloads the page
 */
export async function cleanupAndReload() {
  await unregisterServiceWorkers();
  reloadPageFresh();
}

/**
 * Checks if the current page load is after a payment return
 * @returns {boolean} True if returning from payment
 */
export function isPaymentReturn() {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.has('payment_success') ||
         urlParams.has('payment_failed') ||
         urlParams.has('cache_bust');
}

/**
 * Initializes payment return handling
 * Call this in your main.js/main.ts file
 */
export function initPaymentReturnHandling() {
  // Check if we're returning from a payment
  if (isPaymentReturn()) {
    // Add a small delay to ensure the page has loaded
    setTimeout(() => {
      unregisterServiceWorkers();
    }, 1000);
  }
}
